import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import NutrientCategory from 'classes/StandingData/NutrientCategory/Search/NutrientCategory';
import NutrientCategoryList from 'classes/StandingData/NutrientCategory/Search/NutrientCategoryList';
import Search from 'classes/StandingData/NutrientCategory/Search/Search';
import NutrientCategoryDetailComponent from 'components/desktop/StandingData/NutrientCategory/Detail/NutrientCategoryDetailComponent';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import StandingDataList, {
	Row,
} from 'components/desktop/_general/StandingDataList/StandingDataList';
import clone from 'functions/clone';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import useAirNavigation from 'hooks/useAirNavigation';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { ISelectItem } from 'interfaces/ISelectItem';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 5;

const NutrientCategoryManagementComponent = (props: IProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { params } = useAirNavigation();
	const displayOverlay = params.get('id') ? true : false;

	const [nutrientCategoryList, setNutrientCategoryList] = useState<NutrientCategoryList>(
		new NutrientCategoryList()
	);
	const [search, setSearch] = useState<Search>(new Search());
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode]);

	const initialize = async (): Promise<void> => {
		if (props.archive !== undefined && props.archive == true) {
			search.showOnlyDeleted = true;
		}
		if (window.location.href.includes('?')) {
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getNutrientCategories();
		} else {
			await getNutrientCategories();
		}
		setSearch(clone(search));
	};

	const handleRestoreNutrientCategory = async (
		nutrientCategory: NutrientCategory
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:RESTORE_TITLE'),
			text: t('standingData:NUTRIENTCATEGORY_RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await nutrientCategory.callApiRestore();
				await Swal.fire({
					title: t('standingData:NUTRIENTCATEGORY_RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getNutrientCategories();
			} catch {
				setIsLoading(false);
			}
		}
	};

	const handleArchiveNutrientCategory = async (
		nutrientCategory: NutrientCategory
	): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('standingData:ARCHIVE_TITLE'),
			text: t('standingData:NUTRIENTCATEGORY_ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});
		if (result.value) {
			setIsLoading(true);
			try {
				await nutrientCategory.callApiDelete();
				await Swal.fire({
					title: t('standingData:NUTRIENTCATEGORY_ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
				await getNutrientCategories();
			} catch {
				setIsLoading(false);
			}
		}
	};

	const getNutrientCategories = async () => {
		setIsLoading(true);
		const response = await nutrientCategoryList.callApi(search.mapToRequest());
		response.do((x) => {
			nutrientCategoryList.mapFromApi(x);
			setNutrientCategoryList(clone(nutrientCategoryList));
		});
		setIsLoading(false);
	};

	const handleChangeId = (id: string | null) => {
		search.id.value = id;
		handleChangeUrl();
	};

	const handleOverlayOpen = (input: string): void => {
		handleChangeId(input);
	};

	const handleOverlayClose = (): void => {
		handleChangeId(null);
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (url) navigate(url);
		getNutrientCategories();
	};

	const handleSearch = (searchTerm?: string) => {
		search.setSearchTerm(searchTerm ?? '');
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const handleChangeIdToNew = (): void => {
		handleChangeId('new');
	};

	const getSuggestedNutrientCategories = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await nutrientCategoryList.search(searchTerm, props.archive ?? false);
		} else {
			nutrientCategoryList.filtered = [];
		}

		setNutrientCategoryList(clone(nutrientCategoryList));
		return nutrientCategoryList.filtered;
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleOverlayOpen(id),
			};
		}
	};

	const getButtonDataArchive = (input: NutrientCategory): BtnData | undefined => {
		if (!props.archive) {
			return {
				onClick: () => handleArchiveNutrientCategory(input),
			};
		}
	};

	const getButtonDataRestore = (input: NutrientCategory): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreNutrientCategory(input),
			};
		}
	};

	const renderButtons = (input: NutrientCategory): JSX.Element => {
		return (
			<BtnGroup
				edit={getButtonDataEdit(input.id)}
				archive={getButtonDataArchive(input)}
				restore={getButtonDataRestore(input)}
			/>
		);
	};

	const renderListRows = (): Row[] => {
		return nutrientCategoryList.all.map((nutrientCategory: NutrientCategory) => {
			return {
				id: nutrientCategory.id,
				jsx: (
					<div
						className="d-flex flex-row justify-content-start align-items-center"
						style={{ gap: '2em' }}
					>
						<div
							className={
								nutrientCategory.parentName ? 'd-flex flex-row' : 'flex-fill'
							}
							style={{ gap: '0.8em' }}
						>
							<span className="grey">{t('standingData:NAME')}: </span>
							{nutrientCategory.name}
						</div>
						<div>
							<span className="grey">{t('standingData:SORT_ORDER')}: </span>
							{nutrientCategory.sortOrder}
						</div>
						{nutrientCategory.parentName && (
							<div className="flex-fill">
								<span className="grey">{t('standingData:PARENT')}: </span>
								{nutrientCategory.parentName}
							</div>
						)}
						<div>{renderButtons(nutrientCategory)}</div>
					</div>
				),
			};
		});
	};

	const renderContent = (): JSX.Element => {
		return (
			<main className="container">
				<div className="d-flex flex-row justify-content-between">
					{!props.archive ? (
						<>
							<h1>{t('standingData:NUTRIENTCATEGORY_MANAGEMENT')}</h1>
							<div className="d-flex flex-gap-25">
								<BtnNew
									onClick={handleChangeIdToNew}
									name={t('standingData:CREATE_NUTRIENTCATEGORY')}
								/>
							</div>
						</>
					) : (
						<h1>{t('standingData:NUTRIENTCATEGORY_ARCHIVE')}</h1>
					)}
				</div>
				<br />
				<div className="row">
					<div className="col-lg-12">
						<div className="input-group mb-3">
							<AdvancedSearchInput
								debounceMilliSeconds={350}
								getSuggestedItems={getSuggestedNutrientCategories}
								handleSearch={handleSearch}
								searchTerm={searchTerm}
								setSearchTerm={setSearchTerm}
								triggerSearchLetterAmount={1}
							/>
						</div>
					</div>
				</div>
				<br />
				<LoadingAnimation isLoading={isLoading} />
				<p>{nutrientCategoryList.totalCount + ' ' + t('_general:NUTRIENTCATEGORIES')}</p>
				<StandingDataList
					archive={props.archive}
					dataComponentType="nutritional categories"
					displayOverlay={displayOverlay}
					rows={renderListRows()}
					overlay={<NutrientCategoryDetailComponent />}
					handleOverlayOpen={handleOverlayOpen}
					handleOverlayClose={handleOverlayClose}
				/>
				<br />
				<Pagination
					countOfAllListItems={nutrientCategoryList.totalCount}
					pageIndex={search.getPageIndex()}
					setPageIndex={handleChangePageIndex}
					listItemAmount={search.getPageSize()}
					maxNumberOfButtons={maxNumberOfButtons}
				/>
			</main>
		);
	};

	return <>{renderContent()}</>;
};

export default NutrientCategoryManagementComponent;
